import React, { useEffect, useRef, useState, useCallback } from "react";
import { ActionBar, Button, HStack, Separator, Text, VStack } from "@chakra-ui/react";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import { getCombinedKeysText, matchKeyInText } from "../../../utilies/Utility";
import { useMapObjects } from "../../../context/MapObjectsContext";
import { toggleDrivewayTool, undoDrivewayTool } from "../../../utilies/driveway/Add";
import Type from "../../../utilies/Types";
import HoDrivewayRoad from "../../../icons/HoDriveway";
import { LuUndo, LuX } from "react-icons/lu";

// Layer keys
const TURNING_LINE_KEY = "turningline";
const VIRTUAL_LINE_KEY = "virtualline";
const LANE_MARKER_KEY = "lanemarker";
const LANELET_KEY = "lanelet";

function DrivewayTool() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);
  const userData = useAppStore((s) => s.userData);

  const drivewayMode = useAppStore((s) => s.drivewayMode);
  const setDrivewayMode = useAppStore((s) => s.setDrivewayMode);
  const savedHotkeysRef = useAppStore((s) => s.savedHotkeysRef);

  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();

  const [isShow, setIsShow] = useState(false);
  const onEdit = useCallback(
    (payload) => {
      const changedDs = Array.isArray(payload?.changedDataSources) ? payload.changedDataSources : [];
      const changedKeys = Array.isArray(payload?.changedLayerKeys) ? payload.changedLayerKeys : [];

      if (!changedDs.length && !changedKeys.length) return;

      const dsSet = new Set(changedDs);

      setMapObjects((prev) => {
        const markEdited = (obj) => {
          if (!obj) return obj;

          const dsMatch = obj?.dataSource && dsSet.has(obj.dataSource);
          const keyMatch = obj?.file_name && changedKeys.some((k) => matchKeyInText(obj.file_name, k));

          if (dsMatch || keyMatch) return { ...obj, is_edited: true };
          return obj;
        };

        if (Array.isArray(prev)) return prev.map(markEdited);

        if (prev && typeof prev === "object") {
          const next = { ...prev };
          for (const k in next) next[k] = markEdited(next[k]);
          return next;
        }

        return prev;
      });
    },
    [setMapObjects],
  );

  // last known datasources
  const dsRef = useRef({
    turningLine: null,
    virtualLine: null,
    laneMarker: null,
    lanelet: null,
  });

  const findDataSources = useCallback(() => {
    const t = {
      turningLine: null,
      virtualLine: null,
      laneMarker: null,
      lanelet: null,
    };

    for (const key in mapObjects) {
      const obj = mapObjects[key];
      if (!obj?.file_name) continue;

      if (matchKeyInText(obj.file_name, TURNING_LINE_KEY)) t.turningLine = obj.dataSource;
      if (matchKeyInText(obj.file_name, VIRTUAL_LINE_KEY)) t.virtualLine = obj.dataSource;
      if (matchKeyInText(obj.file_name, LANE_MARKER_KEY)) t.laneMarker = obj.dataSource;
      if (matchKeyInText(obj.file_name, LANELET_KEY)) t.lanelet = obj.dataSource;
    }

    return t;
  }, [mapObjects]);

  useEffect(() => {
    const t = findDataSources();
    dsRef.current = t;
    setIsShow(Boolean(t.turningLine && t.virtualLine && t.laneMarker && t.lanelet));
  }, [findDataSources]);

  const disableTool = useCallback(() => {
    const { turningLine, virtualLine, laneMarker, lanelet } = dsRef.current || {};
    try {
      toggleDrivewayTool(viewerRef.current, onEdit, true, turningLine, virtualLine, laneMarker, lanelet);
    } catch {}
  }, [viewerRef, onEdit]);

  const enableTool = useCallback(() => {
    const t = findDataSources();
    dsRef.current = t;

    if (!t.turningLine || !t.virtualLine || !t.laneMarker || !t.lanelet) {
      setDrivewayMode(false);
      disableTool();
      return false;
    }

    toggleDrivewayTool(viewerRef.current, onEdit, false, t.turningLine, t.virtualLine, t.laneMarker, t.lanelet, userData, mapObjectsRef, setMapObjects);

    return true;
  }, [findDataSources, viewerRef, setDrivewayMode, disableTool, onEdit]);

  // 🔑 SINGLE SOURCE OF TRUTH
  const toggleFully = useCallback(
    (force) => {
      const next = typeof force === "boolean" ? force : !drivewayMode;

      if (next) {
        const ok = enableTool();
        if (ok) setDrivewayMode(true);
      } else {
        setDrivewayMode(false);
        disableTool();
      }
    },
    [drivewayMode, enableTool, disableTool, setDrivewayMode],
  );

  // sync when store changes externally
  useEffect(() => {
    if (!drivewayMode) disableTool();
  }, [drivewayMode, disableTool]);

  // auto-disable if layers disappear
  useEffect(() => {
    if (!isShow && drivewayMode) {
      toggleFully(false);
    }
  }, [isShow, drivewayMode, toggleFully]);

  // cleanup
  useEffect(() => {
    return () => disableTool();
  }, [disableTool]);

  // --------------------
  // HOTKEY HANDLING
  // --------------------
  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const normalizeKey = (k) => (k === " " ? "space" : String(k).toLowerCase());

    const getCombo = (type) => savedHotkeysRef?.current?.find((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo.length) return false;

      if (e.shiftKey !== combo.includes("shift")) return false;
      if (e.ctrlKey !== combo.includes("ctrl")) return false;
      if (e.altKey !== combo.includes("alt")) return false;
      if (e.metaKey !== combo.includes("meta")) return false;

      const main = combo.find((k) => !isMod(k));
      return normalizeKey(e.key) === normalizeKey(main);
    };

    const shouldIgnore = (e) => ["input", "textarea"].includes(e.target?.tagName?.toLowerCase()) || e.target?.isContentEditable;

    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));

    // Pending state for 3-key sequential combos like: ["ctrl","d","r"]
    const pendingRef = { current: null };
    // keep stable ref on window scope by storing on window symbol (so effect re-renders don't break it)
    const PENDING_KEY = "__driveway_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = { active: false, mods: [], stepKey: "", finalKey: "", expiresAt: 0, tid: null };
    }
    pendingRef.current = window[PENDING_KEY];

    const resetPending = () => {
      const p = pendingRef.current;
      if (!p) return;
      if (p.tid) clearTimeout(p.tid);
      p.active = false;
      p.mods = [];
      p.stepKey = "";
      p.finalKey = "";
      p.expiresAt = 0;
      p.tid = null;
    };

    const startPending = (mods, stepKey, finalKey) => {
      const p = pendingRef.current;
      if (!p) return;

      resetPending();

      p.active = true;
      p.mods = mods;
      p.stepKey = stepKey;
      p.finalKey = finalKey;
      p.expiresAt = Date.now() + 1000;

      p.tid = setTimeout(() => {
        resetPending();
      }, 1000);
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const onKeyDown = (e) => {
      if (shouldIgnore(e)) return;
      if (editMode || !isShow) return;

      // Escape logic stays the same
      if (e.key === "Escape" && drivewayMode) {
        e.preventDefault();
        resetPending();
        toggleFully(false);
        return;
      }

      const combo = getCombo(Type.HOTKEYS.DRIVEWAY);

      // Detect special 3-key combo with 2 English letters (e.g. ctrl + d, then within 1000ms press r)
      const mods = combo.map(normalizeKey).filter(isMod);
      const nonMods = combo.map(normalizeKey).filter((k) => !isMod(k));
      const isSequential3 = combo.length === 3 && mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);

      const p = pendingRef.current;

      // If we are in pending window, check for final key
      if (p?.active) {
        if (Date.now() > p.expiresAt) {
          resetPending();
        } else {
          const k = normalizeKey(e.key);
          // final key can be pressed with same modifier(s) still held OR with none (ctrl+d then release then r)
          const okMods = modsMatchExactly(e, p.mods) || modsAreNone(e);

          if (okMods && k === normalizeKey(p.finalKey)) {
            e.preventDefault();
            resetPending();
            toggleFully();
            return;
          }

          // Any other non-mod key cancels pending
          if (!isMod(k)) resetPending();
        }
      }

      // Handle sequential start: mods + first letter
      if (isSequential3) {
        const stepKey = nonMods[0];
        const finalKey = nonMods[1];

        if (modsMatchExactly(e, mods) && normalizeKey(e.key) === normalizeKey(stepKey)) {
          e.preventDefault();
          startPending(mods, stepKey, finalKey);
          return;
        }
      }

      // Fallback to normal one-shot hotkey behavior
      if (matches(e, combo)) {
        e.preventDefault();
        resetPending();
        toggleFully();
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      resetPending();
    };
  }, [editMode, isShow, drivewayMode, savedHotkeysRef, toggleFully]);

  if (!isShow) return null;

  return (
    <>
      <HStack>
        <Button
          disabled={editMode}
          p={0}
          bg={drivewayMode ? "green" : "white"}
          color={drivewayMode ? "white" : "MenuText"}
          borderWidth={drivewayMode ? 2 : 1}
          borderColor={drivewayMode ? "green" : "gray.200"}
          onClick={() => toggleFully()}
          aria-pressed={drivewayMode}
        >
          <Tooltip content={"Driveway " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.DRIVEWAY)}>
            <HoDrivewayRoad />
          </Tooltip>
        </Button>
      </HStack>

      <ActionBar.Root open={drivewayMode} closeOnEscape={false} closeOnInteractOutside={false}>
        <ActionBar.Positioner>
          <ActionBar.Content>
            <VStack gap={0} align="flex-start">
              <Text fontWeight="semibold" fontSize="sm">
                Driveway Tool
              </Text>
              <Separator my={2} width={"full"} />
              <HStack>
                <Button p={0} bg={"white"} color={"MenuText"} borderWidth={1} borderColor={"gray.200"} onClick={() => undoDrivewayTool()}>
                  <Tooltip content={"Undo"}>
                    <LuUndo />
                  </Tooltip>
                </Button>
                <Button p={0} variant={"outline"} colorPalette={"red"} borderWidth={1} borderColor={"gray.200"} onClick={() => toggleFully(false)}>
                  <Tooltip content={"Close"}>
                    <LuX />
                  </Tooltip>
                </Button>
              </HStack>
            </VStack>
          </ActionBar.Content>
        </ActionBar.Positioner>
      </ActionBar.Root>
    </>
  );
}

export default DrivewayTool;
